import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Settings, Shield, Database, Globe, Mail, CreditCard,
  RefreshCw, CheckCircle2, XCircle, AlertTriangle, Plug, Wand2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function IntegrationsPage() {
  const [scannerStatus, setScannerStatus] = useState({});
  const [threatStatus, setThreatStatus] = useState({});
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('grc_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [scannerRes, threatRes, connectorsRes] = await Promise.all([
        axios.get(`${API}/api/scanners/status`, { headers }),
        axios.get(`${API}/api/threat-intel/feeds/status`, { headers }),
        axios.get(`${API}/api/connectors`, { headers })
      ]);

      setScannerStatus(scannerRes.data);
      setThreatStatus(threatRes.data);
      setConnectors(connectorsRes.data);
    } catch (error) {
      toast.error('Failed to load integration status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const testScanner = async (scannerType) => {
    setTesting(scannerType);
    try {
      const token = localStorage.getItem('grc_token');
      const res = await axios.post(`${API}/api/scanners/${scannerType}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.connected) {
        toast.success(`${scannerType} connected successfully`);
      } else {
        toast.error(res.data.error || 'Connection failed');
      }
      fetchData();
    } catch (error) {
      toast.error(`Failed to test ${scannerType}`);
    } finally {
      setTesting(null);
    }
  };

  const testConnector = async (connectorId) => {
    setTesting(connectorId);
    try {
      const token = localStorage.getItem('grc_token');
      const res = await axios.post(`${API}/api/connectors/${connectorId}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Connection tested');
      fetchData();
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTesting(null);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'connected' || status === 'active') {
      return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    } else if (status === 'not_configured') {
      return <XCircle className="h-5 w-5 text-gray-400" />;
    }
    return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
  };

  const getStatusBadge = (status, configured) => {
    if (configured && status === 'connected') {
      return <Badge className="bg-green-100 text-green-800">Connected</Badge>;
    } else if (configured) {
      return <Badge className="bg-yellow-100 text-yellow-800">Configured</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-800">Not Configured</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const scanners = [
    {
      id: 'nessus',
      name: 'Tenable Nessus',
      description: 'Enterprise vulnerability scanner',
      status: scannerStatus.nessus,
      envVars: ['NESSUS_URL', 'NESSUS_ACCESS_KEY', 'NESSUS_SECRET_KEY']
    },
    {
      id: 'qualys',
      name: 'Qualys Cloud Platform',
      description: 'Cloud-based vulnerability management',
      status: scannerStatus.qualys,
      envVars: ['QUALYS_API_URL', 'QUALYS_USERNAME', 'QUALYS_PASSWORD']
    },
    {
      id: 'openvas',
      name: 'OpenVAS/Greenbone',
      description: 'Open-source vulnerability scanner',
      status: scannerStatus.openvas,
      envVars: ['OPENVAS_HOST', 'OPENVAS_USERNAME', 'OPENVAS_PASSWORD']
    }
  ];

  const threatFeeds = [
    {
      id: 'nvd',
      name: 'NVD (NIST)',
      description: 'National Vulnerability Database',
      status: threatStatus.nvd
    },
    {
      id: 'mitre_attack',
      name: 'MITRE ATT&CK',
      description: 'Adversary tactics and techniques',
      status: threatStatus.mitre_attack
    },
    {
      id: 'alienvault_otx',
      name: 'AlienVault OTX',
      description: 'Open Threat Exchange',
      status: threatStatus.alienvault_otx
    }
  ];

  return (
    <div className="space-y-6" data-testid="integrations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Integrations</h1>
          <p className="text-muted-foreground">Manage your security tool integrations and API connections</p>
        </div>
        <Link to="/integration-wizard">
          <Button data-testid="launch-wizard-btn">
            <Wand2 className="mr-2 h-4 w-4" />
            Setup Wizard
          </Button>
        </Link>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="scanners" className="space-y-4">
        <TabsList>
          <TabsTrigger value="scanners" data-testid="scanners-tab">
            <Shield className="mr-2 h-4 w-4" /> Vulnerability Scanners
          </TabsTrigger>
          <TabsTrigger value="threat-intel" data-testid="threat-intel-tab">
            <Database className="mr-2 h-4 w-4" /> Threat Intelligence
          </TabsTrigger>
          <TabsTrigger value="connectors" data-testid="connectors-tab">
            <Plug className="mr-2 h-4 w-4" /> Data Connectors
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="notifications-tab">
            <Mail className="mr-2 h-4 w-4" /> Notifications
          </TabsTrigger>
        </TabsList>

        {/* Scanners Tab */}
        <TabsContent value="scanners">
          <div className="grid gap-4">
            {scanners.map((scanner) => (
              <Card key={scanner.id} data-testid={`scanner-${scanner.id}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {getStatusIcon(scanner.status?.status)}
                      <div>
                        <CardTitle>{scanner.name}</CardTitle>
                        <CardDescription>{scanner.description}</CardDescription>
                      </div>
                    </div>
                    {getStatusBadge(scanner.status?.status, scanner.status?.configured)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-accent/50 rounded-lg">
                      <p className="text-sm font-medium mb-2">Required Environment Variables:</p>
                      <div className="flex flex-wrap gap-2">
                        {scanner.envVars.map((env) => (
                          <Badge key={env} variant="outline" className="font-mono text-xs">
                            {env}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => testScanner(scanner.id)}
                        disabled={testing === scanner.id}
                        data-testid={`test-${scanner.id}`}
                      >
                        {testing === scanner.id ? (
                          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Settings className="mr-2 h-4 w-4" />
                        )}
                        Test Connection
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Threat Intel Tab */}
        <TabsContent value="threat-intel">
          <div className="grid gap-4">
            {threatFeeds.map((feed) => (
              <Card key={feed.id} data-testid={`feed-${feed.id}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {getStatusIcon(feed.status?.status)}
                      <div>
                        <CardTitle>{feed.name}</CardTitle>
                        <CardDescription>{feed.description}</CardDescription>
                      </div>
                    </div>
                    {getStatusBadge(feed.status?.status, feed.status?.configured)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {feed.status?.requires_api_key ? (
                        <span className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-yellow-500" />
                          API Key Required
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          No API Key Required
                        </span>
                      )}
                    </div>
                    <Badge variant="outline">
                      {feed.status?.status === 'active' ? 'Active' : 'Ready'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Connectors Tab */}
        <TabsContent value="connectors">
          <Card>
            <CardHeader>
              <CardTitle>Data Connectors</CardTitle>
              <CardDescription>Connect to external data sources for automated evidence collection</CardDescription>
            </CardHeader>
            <CardContent>
              {connectors.length > 0 ? (
                <div className="space-y-4">
                  {connectors.map((connector) => (
                    <div
                      key={connector.id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                      data-testid={`connector-${connector.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <Globe className="h-5 w-5 text-primary" />
                        <div>
                          <p className="font-medium">{connector.name}</p>
                          <p className="text-sm text-muted-foreground capitalize">{connector.type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <Badge variant={connector.status === 'connected' ? 'default' : 'secondary'}>
                          {connector.status}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => testConnector(connector.id)}
                          disabled={testing === connector.id}
                        >
                          {testing === connector.id ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            'Test'
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Plug className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No connectors configured</p>
                  <p className="text-sm">Add connectors to automate evidence collection</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <div className="grid gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" /> Email (Resend)
                </CardTitle>
                <CardDescription>Email notifications for alerts and reports</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-accent/50 rounded-lg mb-4">
                  <p className="text-sm font-medium mb-2">Required:</p>
                  <Badge variant="outline" className="font-mono text-xs">RESEND_API_KEY</Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Configure RESEND_API_KEY in your environment to enable email notifications for SLA warnings, 
                  compliance alerts, and weekly digest reports.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" /> Slack
                </CardTitle>
                <CardDescription>Real-time notifications to Slack channels</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-accent/50 rounded-lg mb-4">
                  <p className="text-sm font-medium mb-2">Required:</p>
                  <Badge variant="outline" className="font-mono text-xs">SLACK_WEBHOOK_URL</Badge>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" /> Microsoft Teams
                </CardTitle>
                <CardDescription>Real-time notifications to Teams channels</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-accent/50 rounded-lg mb-4">
                  <p className="text-sm font-medium mb-2">Required:</p>
                  <Badge variant="outline" className="font-mono text-xs">TEAMS_WEBHOOK_URL</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
